import _extends from '@babel/runtime/helpers/esm/extends';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import React, { useMemo, useRef, useState, useLayoutEffect } from 'react';
import { css, useTheme, jsx } from '@emotion/react';
import _taggedTemplateLiteral from '@babel/runtime/helpers/esm/taggedTemplateLiteral';
import { math, rgba } from 'polished';
import { textStyles } from '@paypalcorp/pp-react-text';
import { devWarning, utilStyles, throttle, hasNavigator, KeyboardCode } from '@paypalcorp/pp-react-utils';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import { IconButton } from '@paypalcorp/pp-react-icon-button';
import { ArrowLeftIcon, ArrowRightIcon } from '@paypalcorp/pp-react-icons';
var _templateObject, _templateObject2, _templateObject3, _templateObject4, _templateObject5, _templateObject6, _templateObject7, _templateObject8, _templateObject9, _templateObject10, _templateObject11, _templateObject12, _templateObject13, _templateObject14, _templateObject15, _templateObject16, _templateObject17, _templateObject18, _templateObject19, _templateObject20;
function styles(tokens) {
  /* istanbul ignore next */
  if (!Object.keys(tokens).length) {
    devWarning('Missing Emotion theme. Use ThemeProvider from @emotion/react to pass a valid theme from pp-react.');
    return {};
  }
  var sysColorBackgroundSurface = tokens.sysColorBackgroundSurface,
    sysBorderThicknessEmphasis = tokens.sysBorderThicknessEmphasis,
    sysSpaceXl = tokens.sysSpaceXl,
    sysColorStructureFocus = tokens.sysColorStructureFocus,
    sysMotionDurationMoveSmall = tokens.sysMotionDurationMoveSmall,
    sysColorPrimaryMain = tokens.sysColorPrimaryMain,
    sysSizeFieldSm = tokens.sysSizeFieldSm,
    sysSpaceLg = tokens.sysSpaceLg,
    sysSpaceMd = tokens.sysSpaceMd,
    sysSpaceJumbo = tokens.sysSpaceJumbo,
    sysBorderRadiusLg = tokens.sysBorderRadiusLg,
    sysColorPrimaryMainHover = tokens.sysColorPrimaryMainHover,
    sysColorPrimaryMainActive = tokens.sysColorPrimaryMainActive,
    sysSizeMediaSm = tokens.sysSizeMediaSm,
    sysSizeTapTargetDefault = tokens.sysSizeTapTargetDefault;
  var _utilStyles = utilStyles(tokens),
    elevationStyles = _utilStyles.elevationStyles,
    focusStyles = _utilStyles.focusStyles,
    variables = _utilStyles.variables;
  var _textStyles = textStyles(tokens),
    textBody = _textStyles.body;
  var tabPaddingLeft = math("".concat(sysSpaceJumbo, " + ").concat(sysSpaceXl));
  var noDisplay = css(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n    display: none;\n  "])));
  var blockDisplay = css(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n    display: block;\n  "])));
  var tabWrapperBase = css(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n    label: tabs_wrapper_base;\n    position: relative;\n  "])));
  var base = css(_templateObject4 || (_templateObject4 = _taggedTemplateLiteral(["\n    label: tabs_base;\n    overflow-y: hidden;\n    white-space: nowrap;\n    position: relative;\n    scrollbar-width: none;\n    padding: ", " ", " ", " ", ";\n    &::-webkit-scrollbar {\n      ", "\n    }\n  "])), sysSpaceXl, sysSpaceXl, sysSpaceXl, tabPaddingLeft, noDisplay);
  var tabsWrapper = css(_templateObject5 || (_templateObject5 = _taggedTemplateLiteral(["\n    label: tabs_wrapper;\n    display: flex;\n    float: left;\n    min-height: ", ";\n    align-items: center;\n    width: max-content;\n    position: relative;\n    [dir='rtl'] & {\n      float: right;\n    }\n  "])), sysSizeFieldSm);
  var tabsCenter = css(_templateObject6 || (_templateObject6 = _taggedTemplateLiteral(["\n    label: tabs_center;\n    min-width: 100%;\n    width: auto;\n    min-height: ", ";\n    justify-content: center;\n    @media (max-width: ", ") {\n      width: auto;\n      justify-content: flex-start;\n    }\n  "])), sysSizeFieldSm, sysSizeMediaSm);
  var tabsBtn = css(_templateObject7 || (_templateObject7 = _taggedTemplateLiteral(["\n    label: tabs_btn;\n    ", "\n    padding: ", " ", ";\n    min-height: ", ";\n    min-width: ", ";\n    max-width: ", ";\n    margin-inline-end: ", ";\n    outline: none;\n    position: relative;\n    border: none;\n    background-color: transparent;\n    white-space: normal;\n    word-wrap: break-word;\n    text-decoration: none;\n    flex-shrink: 0;\n    ", "\n    align-items: center;\n    cursor: pointer;\n    border-radius: ", ";\n    z-index: 1;\n    &:last-of-type {\n      margin-inline-end: 0;\n    }\n    &:before {\n      left: 0;\n      bottom: 0;\n      right: 0;\n      position: relative;\n      ", "\n      content: attr(data-button-text);\n      height: 0;\n      overflow: hidden;\n      visibility: hidden;\n      pointer-events: none;\n    }\n    &:after {\n      content: '';\n      position: absolute;\n      inset: 0;\n      border: none;\n      border-radius: ", ";\n      pointer-events: none;\n    }\n    &:hover {\n      text-decoration: none;\n\n      &:after {\n        border: ", " solid ", ";\n      }\n    }\n    &:active {\n      border-radius: ", ";\n      background: ", ";\n      text-decoration: none;\n      &:after {\n        border: ", " solid ", ";\n      }\n    }\n    &:focus {\n      outline: none;\n      text-decoration: none;\n      ", "\n      transform: none;\n      transition: none;\n    }\n    &:visited {\n      text-decoration: none;\n    }\n  "])), textBody(), sysSpaceMd, sysSpaceLg, sysSizeFieldSm, '5.25rem', '11.5rem', sysSpaceLg, blockDisplay, math("".concat(sysBorderRadiusLg, " * 2")), blockDisplay, math("".concat(sysBorderRadiusLg, " * 2")), sysBorderThicknessEmphasis, sysColorPrimaryMainHover, math("".concat(sysBorderRadiusLg, " * 2")), sysColorBackgroundSurface, sysBorderThicknessEmphasis, sysColorPrimaryMainActive, focusStyles.focusOffsetElement);
  var tabSlider = css(_templateObject8 || (_templateObject8 = _taggedTemplateLiteral(["\n    label: slider;\n    position: absolute;\n    top: 0;\n    left: 0;\n    width: 0;\n    height: 100%;\n    ", "\n    -webkit-text-stroke-width: 0.0421875rem;\n    border-radius: ", ";\n    transition: all ", " ", ";\n  "])), elevationStyles.level1, math("".concat(sysBorderRadiusLg, " * 2")), sysMotionDurationMoveSmall, variables.ppuiEaseMoveSmall);
  var tabsSelectedBtn = css(_templateObject9 || (_templateObject9 = _taggedTemplateLiteral(["\n    label: tab_selected;\n    -webkit-text-stroke-width: 0.0421875rem;\n    color: ", ";\n    &:hover {\n      text-decoration: none;\n      &:after {\n        border: ", " solid ", ";\n      }\n    }\n  "])), sysColorPrimaryMain, sysBorderThicknessEmphasis, sysColorPrimaryMainHover);
  var tabsScrollBtn = css(_templateObject10 || (_templateObject10 = _taggedTemplateLiteral(["\n    label: tabs_scroll_btn;\n    background-color: ", ";\n    position: absolute;\n    display: flex;\n    align-items: center;\n    justify-content: center;\n    opacity: 0;\n    visibility: hidden;\n    width: ", ";\n    height: ", ";\n    top: 0;\n  "])), sysColorBackgroundSurface, sysSizeTapTargetDefault, math("".concat(sysSizeTapTargetDefault, " * 2")));
  var tabsScrollBtnLeft = css(_templateObject11 || (_templateObject11 = _taggedTemplateLiteral(["\n    label: tabs_scroll_btn_left;\n    left: 0;\n    background: linear-gradient(\n      90deg,\n      ", " 47.45%,\n      ", " 100%\n    );\n    z-index: 1;\n  "])), sysColorBackgroundSurface, rgba(255, 255, 255, 0));
  var tabsScrollBtnRight = css(_templateObject12 || (_templateObject12 = _taggedTemplateLiteral(["\n    label: tabs_scroll_btn_right;\n    right: 0;\n    background: linear-gradient(\n      270deg,\n      ", " 47.45%,\n      ", " 100%\n    );\n    z-index: 1;\n  "])), sysColorBackgroundSurface, rgba(255, 255, 255, 0));
  var tabsScrollBtnVisible = css(_templateObject13 || (_templateObject13 = _taggedTemplateLiteral(["\n    label: btn_visible;\n    visibility: visible;\n    opacity: 1;\n    transition: visibility ", ",\n      opacity ", ";\n  "])), sysMotionDurationMoveSmall, sysMotionDurationMoveSmall);
  var tabsArrowIcon = css(_templateObject14 || (_templateObject14 = _taggedTemplateLiteral(["\n    label: tabs_arrow_icon;\n    color: ", ";\n    background: ", ";\n    ", "\n  "])), sysColorPrimaryMain, sysColorBackgroundSurface, elevationStyles.level1);
  var tabContent = css(_templateObject15 || (_templateObject15 = _taggedTemplateLiteral(["\n    label: tabs_content;\n    margin: 0 ", " 0 ", ";\n\n    &:focus {\n      outline: none;\n    }\n    &[aria-hidden='true'] {\n      ", "\n    }\n  "])), sysSpaceXl, math("".concat(sysSpaceXl, " * 3")), noDisplay);
  var tabContentFocus = css(_templateObject16 || (_templateObject16 = _taggedTemplateLiteral(["\n    label: tabs_content_focus;\n    &:focus {\n      box-shadow: 0 0 ", "\n        ", " ", ";\n    }\n  "])), sysBorderThicknessEmphasis, sysBorderThicknessEmphasis, sysColorStructureFocus);
  var hideBtn = css(_templateObject17 || (_templateObject17 = _taggedTemplateLiteral(["\n    label: no_scroll_buttons;\n    ", "\n  "])), noDisplay);
  var tabContentVisible = css(_templateObject18 || (_templateObject18 = _taggedTemplateLiteral(["\n    label: tab_content_visible;\n    ", "\n  "])), blockDisplay);
  var tabContentHidden = css(_templateObject19 || (_templateObject19 = _taggedTemplateLiteral(["\n    label: tab_content_hidden;\n    ", "\n  "])), noDisplay);
  var navBackground = css(_templateObject20 || (_templateObject20 = _taggedTemplateLiteral(["\n    label: tab_background;\n    ", "\n    background: ", ";\n  "])), elevationStyles.level3, sysColorBackgroundSurface);
  return {
    tabWrapperBase: tabWrapperBase,
    base: base,
    tabsWrapper: tabsWrapper,
    tabsCenter: tabsCenter,
    tabsBtn: tabsBtn,
    tabSlider: tabSlider,
    tabsSelectedBtn: tabsSelectedBtn,
    tabsScrollBtn: tabsScrollBtn,
    tabsScrollBtnLeft: tabsScrollBtnLeft,
    tabsScrollBtnRight: tabsScrollBtnRight,
    tabsScrollBtnVisible: tabsScrollBtnVisible,
    tabsArrowIcon: tabsArrowIcon,
    tabContent: tabContent,
    tabContentFocus: tabContentFocus,
    hideBtn: hideBtn,
    tabContentVisible: tabContentVisible,
    tabContentHidden: tabContentHidden,
    navBackground: navBackground
  };
}
var _excluded = ["id", "tabIdx", "label", "tabGroupId", "panelId", "selected", "onClick", "className"];
var Tab = React.forwardRef(function (_ref, ref) {
  var id = _ref.id,
    tabIdx = _ref.tabIdx,
    label = _ref.label,
    tabGroupId = _ref.tabGroupId,
    panelId = _ref.panelId,
    selected = _ref.selected,
    onClick = _ref.onClick,
    className = _ref.className,
    rest = _objectWithoutProperties(_ref, _excluded);
  var tokens = useTheme();
  var _styles = useMemo(function () {
    return styles(tokens);
  }, [tokens]);
  var tabStyles = [_styles.tabsBtn, selected && _styles.tabsSelectedBtn];
  var focusable = selected ? 0 : -1;
  var tabId = id || "".concat(tabGroupId, "-tab").concat(parseInt(tabIdx, 10) + 1);
  return jsx("button", _extends({
    ref: ref,
    "data-tab-index": tabIdx,
    id: tabId,
    css: tabStyles,
    className: className,
    "aria-controls": panelId,
    "aria-selected": selected,
    tabIndex: focusable,
    type: "button",
    role: "tab"
  }, rest, {
    onClick: onClick,
    "data-button-text": label,
    "data-ppui": "true"
  }), label);
});
var _excluded$1 = ["id", "panelId", "label"];
var isRunningOnIEorEdge = function () {
  if (hasNavigator() && (/MSIE 9/i.test(navigator.userAgent) || /MSIE 10/i.test(navigator.userAgent) || /rv:11.0/i.test(navigator.userAgent) || /Edge\/\d./i.test(navigator.userAgent))) {
    return true;
  }
  return false;
};
var getRefCurrent = function (myRef) {
  return /* istanbul ignore next */myRef && myRef.current ? myRef.current : null;
}; // for scrolling with transition

var SPEED = 25;
var DISTANCE = 120;
var STEP = 20; // eslint-disable-next-line max-statements

var Tabs = function (_ref) {
  var _ref$name = _ref.name,
    name = _ref$name === void 0 ? 'TabsGroup' : _ref$name,
    selectedTabIdx = _ref.selectedTabIdx,
    center = _ref.center,
    _ref$tabs = _ref.tabs,
    tabs = _ref$tabs === void 0 ? [] : _ref$tabs,
    onClick = _ref.onClick,
    className = _ref.className,
    tabClassName = _ref.tabClassName,
    tabsWrapperClassName = _ref.tabsWrapperClassName,
    children = _ref.children,
    _ref$scrollLeftLabel = _ref.scrollLeftLabel,
    scrollLeftLabel = _ref$scrollLeftLabel === void 0 ? 'scroll left' : _ref$scrollLeftLabel,
    _ref$scrollRightLabel = _ref.scrollRightLabel,
    scrollRightLabel = _ref$scrollRightLabel === void 0 ? 'scroll right' : _ref$scrollRightLabel,
    keepAlive = _ref.keepAlive,
    lazyLoad = _ref.lazyLoad,
    addBackgroundColor = _ref.addBackgroundColor;
  var tokens = useTheme();
  var _styles = useMemo(function () {
    return styles(tokens);
  }, [tokens]);
  var navEleRef = useRef();
  var containerEleRef = useRef();
  var prevSelectedTabIdxRef = useRef(0);
  var sliderRef = useRef();
  var tabRef = useRef([]);
  var isTabsFocusedRef = useRef(false);
  var focusedIdxRef = useRef();
  var _useState = useState('none'),
    _useState2 = _slicedToArray(_useState, 2),
    overflowDir = _useState2[0],
    setOverflowDir = _useState2[1];
  var getSelectedTabIdx = function () {
    var index = typeof selectedTabIdx === 'string' ? parseInt(selectedTabIdx, 10) : Math.floor(selectedTabIdx);
    if (!selectedTabIdx || !index || index >= tabs.length || index < 0) {
      return 0;
    }
    return index;
  };
  /* istanbul ignore next */

  var determineOverflow = function () {
    var nav = getRefCurrent(navEleRef);
    if (nav) {
      var tabsWrapper = nav.children[0];
      var _scroll = nav.scrollLeft;
      var navRight = Math.floor(nav.getBoundingClientRect().right);
      var tabsRight = Math.floor(tabsWrapper.getBoundingClientRect().right);
      var navLeft = Math.floor(nav.getBoundingClientRect().left);
      var tabsLeft = Math.floor(tabsWrapper.getBoundingClientRect().left);
      if (_scroll === 0 && navRight > tabsRight && tabsLeft > navLeft) {
        setOverflowDir('none');
        return;
      }
      if (tabsLeft < navLeft && navRight < tabsRight) {
        setOverflowDir('both');
        return;
      }
      if (tabsRight > navRight) {
        setOverflowDir('right');
        return;
      }
      if (tabsLeft < navLeft) {
        setOverflowDir('left');
        return;
      }
    } // record that the function was called

    var evt = new Event('checkOverflow');
    window.dispatchEvent(evt);
  };
  var getWrapperCurrent = function (w) {
    return /* istanbul ignore next */w && w.current ? w.current : null;
  }; // apply styles to the slider element based on the
  // dimention of the tab

  var applySliderStyles = function (element) {
    var _element$offsetParent;
    var slider = getWrapperCurrent(sliderRef);
    slider.style.left = "".concat((element === null || element === void 0 ? void 0 : element.getBoundingClientRect().left) - (element === null || element === void 0 ? void 0 : (_element$offsetParent = element.offsetParent) === null || _element$offsetParent === void 0 ? void 0 : _element$offsetParent.getBoundingClientRect().left), "px");
    slider.style.width = "".concat(element === null || element === void 0 ? void 0 : element.getBoundingClientRect().width, "px");
    slider.style.top = "".concat(element === null || element === void 0 ? void 0 : element.offsetTop, "px");
    slider.style.height = "".concat(element === null || element === void 0 ? void 0 : element.offsetHeight, "px");
  };
  var offsetFocus = function (offset) {
    var nav = getRefCurrent(navEleRef);
    var tabDomItems = nav.querySelectorAll('[role="tab"]');
    var nextIndex = focusedIdxRef.current + offset;
    var index;
    /* istanbul ignore next */

    if (nextIndex === tabs.length) {
      index = 0;
    } else if (nextIndex === -1) {
      index = tabs.length - 1;
    } else {
      index = nextIndex;
    }
    tabDomItems[index].focus();
    tabDomItems[index].scrollIntoView({
      block: 'nearest'
    });
    focusedIdxRef.current = index;
  };
  var keyHandler = function (event) {
    /* istanbul ignore next */
    var KeyCode = new KeyboardCode(event.key || event.keyCode);
    var selectedIndex = getSelectedTabIdx();
    if (KeyCode.IS_ARROW_LEFT) {
      event.preventDefault();
      offsetFocus(-1);
    } else if (KeyCode.IS_ARROW_RIGHT) {
      event.preventDefault();
      offsetFocus(1);
    } else if (KeyCode.IS_ENTER || KeyCode.IS_SPACEBAR) {
      event.preventDefault();
      /* istanbul ignore next */

      if (selectedIndex !== focusedIdxRef.current) {
        onClick(event, focusedIdxRef.current);
      }
    }
  };
  var handleFocusOut = function (event) {
    isTabsFocusedRef.current = false;
    var newFocusedItem = event.relatedTarget;
    /* istanbul ignore next */

    if (!newFocusedItem || newFocusedItem.parentElement !== event.currentTarget) {
      // @ts-ignore
      document.removeEventListener('keydown', keyHandler, false); // reset focusedIdx to current selected tab

      focusedIdxRef.current = getSelectedTabIdx(); // add scroll arrows back when focus is out:

      determineOverflow();
    }
  };
  useLayoutEffect(function () {
    var nav = getRefCurrent(navEleRef);
    prevSelectedTabIdxRef.current = getSelectedTabIdx();
    /* istanbul ignore next */

    if (isTabsFocusedRef.current) {
      // @ts-ignore
      document.addEventListener('keydown', keyHandler, false);
    }
    nav.firstChild.addEventListener('focusout',
    // @ts-ignore
    handleFocusOut, false);
    return function () {
      // @ts-ignore
      document.removeEventListener('keydown', keyHandler, false);
      nav.firstChild.removeEventListener('focusout',
      // @ts-ignore
      handleFocusOut, false);
    };
  }, [tabs, selectedTabIdx, onClick]);
  useLayoutEffect(function () {
    var container = getRefCurrent(containerEleRef);
    var nav = getRefCurrent(navEleRef);
    var resizeObserver;
    focusedIdxRef.current = getSelectedTabIdx();
    var scrollButtons = container.querySelectorAll("[data-id=\"scroll-btn\"]");
    if (!isRunningOnIEorEdge()) {
      determineOverflow(); // Check for overflow after window resize:

      /* istanbul ignore next */

      if (window.ResizeObserver) {
        resizeObserver = new ResizeObserver(function () {
          determineOverflow();
        });
        resizeObserver.observe(nav);
      } // adjust left/right arrow buttons height tof the tabs wrapper.

      var tabsWrapper = nav.children[0];
      /* istanbul ignore next */

      var btnHeight = (tabsWrapper.offsetHeight + tabsWrapper.offsetTop * 2).toString();
      /* istanbul ignore next */

      if (scrollButtons && tabsWrapper.offsetHeight !== scrollButtons[0].offsetHeight) {
        scrollButtons[0].style.height = "".concat(btnHeight, "px");
        scrollButtons[1].style.height = "".concat(btnHeight, "px");
      }
    }
    return function () {
      /* istanbul ignore next */
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, []);
  useLayoutEffect(function () {
    // For the first time during initialisation
    focusedIdxRef.current = getSelectedTabIdx();
    /* istanbul ignore next */

    var resizeObserver = window.ResizeObserver ? new ResizeObserver(throttle(function () {
      applySliderStyles(tabRef.current[focusedIdxRef.current]);
    }, 100)) : null;
    /* istanbul ignore next */

    if (window.ResizeObserver) {
      resizeObserver.observe(containerEleRef.current);
    }
    return function () {
      /* istanbul ignore next */
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [selectedTabIdx]);
  var handleClick = function (event) {
    var nav = getRefCurrent(navEleRef);
    var tabDomItems = nav.querySelectorAll('[role="tab"]');
    applySliderStyles(event.target);
    isTabsFocusedRef.current = false;
    /* istanbul ignore next */

    if (tabDomItems[focusedIdxRef.current]) {
      tabDomItems[focusedIdxRef.current].blur();
    }
    var tabIdxClicked = parseInt(event.currentTarget.getAttribute('data-tab-index'), 10); // This is tested, but coverage not registering it

    /* istanbul ignore next */

    if (tabIdxClicked) {
      focusedIdxRef.current = tabIdxClicked;
    }
    onClick(event, tabIdxClicked);
  };
  var handleFocus = function (event) {
    isTabsFocusedRef.current = true;
    var prevFocusedItem = event.relatedTarget;
    if (!prevFocusedItem || prevFocusedItem.parentElement !== event.currentTarget) {
      // @ts-ignore
      document.addEventListener('keydown', keyHandler, false); // remove arrows when navigating with keyboard:

      setOverflowDir('none');
    }
  };
  var handleTouchScroll = function () {
    var isScrolling;
    window.clearTimeout(isScrolling);
    /* istanbul ignore next */

    isScrolling = setTimeout(function () {
      determineOverflow();
    }, 15);
  };
  /* istanbul ignore next */

  var scroll = function (direction) {
    var nav = getRefCurrent(navEleRef); // scroll with transition

    var scrollAmount = 0;
    var slideTimer = window.setInterval(function () {
      if (direction === 'right') {
        nav.scrollLeft += STEP;
      } else {
        nav.scrollLeft -= STEP;
      }
      scrollAmount += STEP;
      if (scrollAmount >= DISTANCE) {
        window.clearInterval(slideTimer);
      }
    }, SPEED);
    setTimeout(function () {
      determineOverflow();
    }, 150); // record that scroll event was called

    var evt = new Event('tabsScrolled');
    window.dispatchEvent(evt);
  };
  var selectedIndex = getSelectedTabIdx();
  var leftScrollCssVal = [_styles.tabsScrollBtn, _styles.tabsScrollBtnLeft, /* istanbul ignore next */
  (overflowDir === 'left' || overflowDir === 'both') && _styles.tabsScrollBtnVisible, isRunningOnIEorEdge() && _styles.hideBtn];
  var rightScrollCssVal = [_styles.tabsScrollBtn, _styles.tabsScrollBtnRight, /* istanbul ignore next */
  (overflowDir === 'right' || overflowDir === 'both') && _styles.tabsScrollBtnVisible, isRunningOnIEorEdge() && _styles.hideBtn];
  var scrollButtonFocus = -1;
  var selectedId;
  var tabItems = tabs.map(function (tab, index) {
    var id = tab.id,
      panelId = tab.panelId,
      label = tab.label,
      otherTabProps = _objectWithoutProperties(tab, _excluded$1); // update animation (only on the selected tab)

    if (index === selectedIndex) {
      selectedId = tab.id || "".concat(name, "-tab").concat(index + 1);
    }
    return jsx(Tab, _extends({
      key: "".concat(name, "-tab").concat(index + 1),
      id: id,
      ref: function ref(el) {
        return tabRef.current[index] = el;
      },
      tabIdx: index.toString(),
      label: label,
      tabGroupId: name,
      panelId: panelId,
      selected: index === selectedIndex,
      onClick: handleClick,
      className: tabClassName
    }, otherTabProps, {
      "data-ppui": "true"
    }));
  }); // Get selected tab panel and update selected tab panel props

  var tabContentItem;
  if (keepAlive || lazyLoad) {
    tabContentItem = React.Children.toArray(children).map(function (_panel, idx) {
      var isSelected = selectedIndex === idx;
      /* istanbul ignore next */

      return React.cloneElement(_panel, {
        hidden: !isSelected,
        tabId: isSelected ? selectedId : undefined,
        visible: keepAlive ? true : isSelected
      });
    });
  } else {
    var tabPanel = React.Children.toArray(children).filter(function (_panel, idx) {
      return selectedIndex === idx;
    })[0];
    tabContentItem = React.cloneElement(tabPanel, {
      tabId: selectedId,
      hidden: false
    });
  }
  var otherIconButtonAttr = {
    buttonType: 'secondary',
    css: _styles.tabsArrowIcon
  };
  return jsx("div", {
    ref: containerEleRef,
    id: name,
    css: _styles.tabWrapperBase,
    className: className,
    "data-ppui-info": "tabs_3.10.5"
  }, jsx("nav", {
    css: [_styles.base, addBackgroundColor && _styles.navBackground],
    id: "".concat(name, "-nav"),
    ref: navEleRef,
    onScroll: function onScroll() {
      return handleTouchScroll();
    },
    "data-ppui": "true"
  }, jsx("div", {
    id: "".concat(name, "-tabs"),
    className: tabsWrapperClassName,
    css: [_styles.tabsWrapper, center && _styles.tabsCenter],
    role: "tablist",
    onFocus: function onFocus(e) {
      return handleFocus(e);
    },
    onMouseDown: function onMouseDown(e) {
      return e.preventDefault();
    } // remove focus on click
    ,

    "data-ppui": "true"
  }, tabItems, jsx("div", {
    ref: sliderRef,
    css: [_styles.tabSlider],
    "data-ppui": "true"
  }))), jsx("span", {
    "data-id": "scroll-btn",
    css: leftScrollCssVal,
    "data-ppui": "true"
  }, jsx(IconButton, _extends({
    inverse: true,
    svgIconComponent: ArrowLeftIcon,
    "aria-label": scrollLeftLabel,
    tabIndex: scrollButtonFocus,
    onClick: function () {
      return scroll('left');
    }
  }, otherIconButtonAttr, {
    "data-ppui": "true"
  }), scrollLeftLabel)), jsx("span", {
    "data-id": "scroll-btn",
    css: rightScrollCssVal,
    "data-ppui": "true"
  }, jsx(IconButton, _extends({
    inverse: true,
    svgIconComponent: ArrowRightIcon,
    tabIndex: scrollButtonFocus,
    onClick: function () {
      return scroll('right');
    }
  }, otherIconButtonAttr, {
    "data-ppui": "true"
  }), scrollRightLabel)), tabContentItem);
};
var _excluded$2 = ["id", "tabId", "hidden", "className", "visible", "children"];
var TabPanel = function (_ref) {
  var id = _ref.id,
    tabId = _ref.tabId,
    hidden = _ref.hidden,
    className = _ref.className,
    _ref$visible = _ref.visible,
    visible = _ref$visible === void 0 ? true : _ref$visible,
    children = _ref.children,
    rest = _objectWithoutProperties(_ref, _excluded$2);
  var hasMouseClickFocus = useRef(false);
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    mouseFocus = _useState2[0],
    updateMouseFocus = _useState2[1];
  var tokens = useTheme();
  var _styles = useMemo(function () {
    return styles(tokens);
  }, [tokens]); // Ref<boolean> variable to determine whether to render the component or not.

  var hasRendered = useRef(visible);
  /* istanbul ignore next */

  if (visible && !hasRendered.current) {
    hasRendered.current = true;
  }
  /* istanbul ignore next */

  if (!hasRendered.current) {
    // Do not re-render the TabPanel when its already present in the DOM.
    return null;
  } // to prevent tab panel from getting focus on mouse-click

  var tabContentStyles = [_styles.tabContent, !mouseFocus && _styles.tabContentFocus, /* istanbul ignore next */
  visible ? _styles.tabContentVisible : _styles.tabContentHidden];
  return jsx("div", _extends({
    id: id,
    css: tabContentStyles,
    className: className,
    "aria-labelledby": tabId,
    "aria-hidden": hidden,
    tabIndex: 0,
    role: "tabpanel",
    onFocus: function handleFocus() {
      var hasClickFocus = hasMouseClickFocus.current ? true : false;
      updateMouseFocus(hasClickFocus);
      hasMouseClickFocus.current = false;
    },
    onMouseDown: function handleMouseDown() {
      hasMouseClickFocus.current = true;
    }
  }, rest, {
    "data-ppui": "true"
  }), children);
};
export { Tab, TabPanel, Tabs, styles as tabsStyles };